/**
 * Frame parsing for a Brain Session's live channel.
 *
 * Tokens are a never-persisted preview, appended optimistically; each durable
 * `message` settles what they drew. A turn may produce several messages, so
 * settling appends to earlier ones rather than replacing them.
 */

import { parseCarouselEvent, parseDisplayFormEvent, parseQuickActions } from './eventParsers';

/**
 * Map the wire's `component_id` to the `form_id` the form parser reads, or the
 * form would arrive with no id for its submission to name.
 */
export function brainFormPayload(data: Record<string, unknown>): Record<string, unknown> {
  if (data.form_id !== undefined || typeof data.component_id !== 'string') return data;
  return { ...data, form_id: data.component_id };
}
import type { StreamCallbacks } from './eventParsers';
import type { ChartData } from './types';

/** One frame published on `ws:brain_session:{id}`. */
export interface BrainSessionFrame {
  event: string;
  sequence?: number | null;
  output_id?: string | null;
  data?: Record<string, unknown> | null;
}

/** What one turn has settled so far; held by the caller so parsing stays pure. */
export interface BrainTurnState {
  /** Text of the turn's `message` rows so far, in the order they arrived. */
  settled: string;
  /** The turn the streamed fragments belong to, once one has said. */
  turnEventId: string | null;
  /**
   * How many of that turn's buffered fragments the reply already holds. A
   * `token` frame carries its `index` in the server's buffer; one below this was
   * drawn by a catch-up replay and is skipped.
   */
  nextToken: number;
}

/** A turn that has settled nothing yet. */
export function emptyTurn(): BrainTurnState {
  return { settled: '', turnEventId: null, nextToken: 0 };
}

/**
 * Dispatch one Brain Session frame to the widget's stream callbacks.
 * `user_message` would draw the visitor's message twice and `phase` is tracked
 * locally, so both are dropped.
 */
export function processBrainSessionEvent(
  frame: BrainSessionFrame,
  state: BrainTurnState,
  callbacks: StreamCallbacks,
): void {
  const data = frame.data ?? {};

  if (frame.event === 'token') {
    const turn = typeof data.turn_event_id === 'string' ? data.turn_event_id : null;
    const index = typeof data.index === 'number' ? data.index : null;
    if (turn !== null && index !== null) {
      if (turn === state.turnEventId && index < state.nextToken) return;
      state.turnEventId = turn;
      state.nextToken = index + 1;
    }
    callbacks.onToken((data.text as string) || '');
    return;
  }

  // The reply so far, for a turn still writing when a dropped follow came back:
  // whatever streamed while it was down is in it, and the fragments that follow
  // are measured from it.
  if (frame.event === 'stream') {
    const text = (data.text as string) || '';
    state.turnEventId = typeof data.turn_event_id === 'string' ? data.turn_event_id : null;
    state.nextToken = typeof data.next_index === 'number' ? data.next_index : 0;
    // The buffer holds the turn's whole stream, so it replaces what is drawn;
    // the turn's `message` rows settle it as they arrive.
    if (text) callbacks.onTextReplace?.(text);
    return;
  }

  if (frame.event === 'message') {
    const text = (data.text as string) || '';
    if (!text) return;
    state.settled = state.settled ? `${state.settled}\n\n${text}` : text;
    callbacks.onTextReplace?.(state.settled);
    return;
  }

  if (frame.event === 'display_carousel' && callbacks.onDisplayCarousel) {
    callbacks.onDisplayCarousel(parseCarouselEvent(data));
    return;
  }

  if (frame.event === 'display_form' && callbacks.onDisplayForm) {
    callbacks.onDisplayForm(parseDisplayFormEvent(brainFormPayload(data)));
    return;
  }

  if (frame.event === 'display_chart' && callbacks.onDisplayChart) {
    callbacks.onDisplayChart(data as unknown as ChartData);
    return;
  }

  if (frame.event === 'turn_failed') {
    callbacks.onTurnFailed?.();
    return;
  }

  // Arrives after `turn_complete`: the suggestions are written once the reply
  // is durable, so there is no loading frame ahead of them to draw a skeleton.
  if (frame.event === 'quick_actions' && callbacks.onQuickActions) {
    callbacks.onQuickActions(parseQuickActions(data));
    return;
  }

  if (frame.event === 'turn_complete') {
    state.settled = '';
    state.turnEventId = null;
    state.nextToken = 0;
    callbacks.onComplete();
  }
}
